<template>
    <div class="pb-line-form">
        <div class="pb-field pb-field--wide">
            <label class="pb-label">Product</label>
            <AsyncSelect
                placeholder="Select Product"
                v-model="data.product"
                :api-service="fetchProductList"
                :format-label="option => option.text"
                :additional-query="additionalQuery"
                label="text"
                ref="productField"
                :disabled="disableQtyField"
            />
        </div>
        <div class="pb-field">
            <label class="pb-label" for="batch_number">Batch Number</label>
            <vField
                v-model="data.batch_number"
                name="batch_number"
                type="text"
                id="batch_number"
                class="form-control"
                placeholder="Batch"
            />
        </div>
        <div class="pb-field">
            <label class="pb-label" for="mfn_date">Mfg. Date</label>
            <vField
                v-model="data.mfn_date"
                name="mfn_date"
                id="mfn_date"
                type="month"
                class="form-control"
                @keyup.enter="handleAddProduct"
            />
        </div>
        <div class="pb-field">
            <label class="pb-label" for="exp_date">Expiry Date</label>
            <vField
                v-model="data.exp_date"
                name="exp_date"
                id="exp_date"
                type="month"
                class="form-control"
            />
        </div>
        <div class="pb-field">
            <label class="pb-label" for="selling_rate">S. Rate</label>
            <vField
                v-model="data.selling_rate"
                name="selling_rate"
                id="selling_rate"
                type="number"
                class="form-control text-end"
            />
        </div>
        <div class="pb-field">
            <label class="pb-label" for="purchase_rate">P. Rate</label>
            <vField
                v-model="data.rate"
                name="rate"
                id="purchase_rate"
                type="number"
                class="form-control text-end"
            />
            <span v-if="data.purchase_rate" class="pb-hint">Last purchase: {{ data.purchase_rate }}</span>
            <span v-if="rateError" class="pb-error">{{ rateError }}</span>
        </div>
        <div class="pb-field">
            <label class="pb-label" for="quantity">Quantity</label>
            <input
                v-model="data.quantity"
                name="quantity"
                id="quantity"
                type="number"
                class="form-control text-end"
                ref="qtyField"
                @keyup.enter="handleAddProduct"
                @blur="validateQty"
                :disabled="disableQtyField"
            />
            <span v-if="quantityError" class="pb-error">{{ quantityError }}</span>
        </div>
        <div class="pb-field">
            <label class="pb-label" for="line_amount">Amount</label>
            <input
                readonly
                :value="blankIfZero(amount)"
                id="line_amount"
                type="text"
                class="form-control text-end"
            >
        </div>
        <div class="pb-field">
            <label class="pb-label" for="discount_percent">Discount %</label>
            <input
                v-model.trim="data.discount_percent"
                type="number"
                class="form-control text-end"
                placeholder="Discount(%)"
                :min="0"
                id="discount_percent"
                @keyup.enter="handleAddProduct"
            >
        </div>
        <div class="pb-field pb-field--wide">
            <label class="pb-label">VAT/GST Type</label>
            <AsyncSelect
                placeholder="Select VAT/GST"
                v-model="data.tax_rate"
                :api-service="fetchTaxRateList"
                :additional-query="taxRateQuery"
                @option:selected="onSelectTaxRate"
            />
        </div>
        <div class="pb-field">
            <label class="pb-label" for="vat">VAT/GST Rate</label>
            <input
                v-model.trim="data.vat"
                type="number"
                class="form-control text-end"
                placeholder="VAT/GST(%)"
                :min="0"
                id="vat"
                @keyup.enter="handleAddProduct"
            >
        </div>
        <div class="pb-field">
            <label class="pb-label" for="vat_amount">VAT/GST Amount</label>
            <input
                v-model.trim="data.vat_amount"
                type="number"
                class="form-control text-end"
                placeholder="VAT/GST Amount"
                :min="0"
                id="vat_amount"
                @keyup.enter="handleAddProduct"
            >
        </div>
        <div class="pb-field">
            <label class="pb-label" for="total_amount">Total Amount</label>
            <input
                readonly
                :value="blankIfZero(totalAmount.toFixed(4))"
                id="total_amount"
                type="text"
                class="form-control text-end"
                @keyup.enter="handleAddProduct"
            >
        </div>
        <div class="pb-field pb-field--action">
            <span class="pb-label" aria-hidden="true">&nbsp;</span>
            <div class="pb-actions">
                <button type="button" class="btn btn-primary" @click="handleAddProduct">
                    <template v-if="isEditing">
                        <i class="fa-solid fa-check"></i>
                        Update
                    </template>
                    <template v-else>
                        <i class="fa-solid fa-plus"></i>
                        Add
                    </template>
                </button>
                <button v-if="isEditing" type="button" class="btn btn-outline-secondary" title="Cancel edit" @click="cancelEdit">
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed, ref} from "@vue/runtime-core";
import {inject, watch} from "vue";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handlePurchaseAndSales from "@/services/modules/purchase";
import handleDropdowns from "@/services/modules/dropdowns";
import {useRoute} from "vue-router";
import {floor} from "lodash";

const singleData = {
    product: null,
    product_name: '',
    product_id: null,
    batch_number: '',
    description: '',
    mfn_date: null,
    exp_date: null,
    selling_rate: null,
    purchase_rate: null,
    rate: null,
    quantity: null,
    discount_percent: null,
    vat: null,
    vat_amount: null,
    tax_rate: null,
    tax_rate_id: null,
    g_total: 0,
}

const additionalQuery = ref({
  product_type: "finished_goods"
})

const showError = inject('showError');
const {fetchProductList, fetchProductStock} = handlePurchaseAndSales()
const {fetchTaxRateList} = handleDropdowns()
const route = useRoute();
const $emit = defineEmits(['handleProductAdd'])
const rateError = ref('')
const quantityError = ref('')
const qtyField = ref(null)
const productField = ref(null)
const updateIndex = ref(null)
const data = ref({...singleData})
const companyQuery = `?company_id=${route.params.companyId}`;
const taxRateQuery = { tax_type: 'vat_gst', applicable_on: 'purchase' };

const amount = computed(() => {
  let amount = (data.value.rate * data.value.quantity);
  data.value.amount = amount;
  return amount.toFixed(4);
})

const toNumber = (value) => Number(value) || 0;

const isBlank = (value) => value === null || value === undefined || value === '';

const blankIfZero = (value) => {
    if (! toNumber(value)) return '';
    return value;
}

const selectedProduct = computed(() => data.value.product)

const disableQtyField = computed(() => data.value.hasOwnProperty('can_edit_qty') && !data.value.can_edit_qty );

const isEditing = computed(() => updateIndex.value !== null && updateIndex.value !== undefined);

const amountAfterDiscount = computed(() => toNumber(amount.value) - toNumber(data.value.discount_amount))

const totalAmount = computed(() => amountAfterDiscount.value + toNumber(data.value.vat_amount))

watch(selectedProduct, (newValue) => {
    if(newValue && newValue.id) {
        fetchProductStock(newValue.id, `${companyQuery}&skip_stock_check=1`).then((res) => {
            if(res.data) {
                data.value.product_id = newValue.id;
                data.value.selling_rate = res.data.sales_rate ?  res.data.sales_rate : 0;
                data.value.product_name = newValue.text;
                data.value.purchase_rate = res.data.purchase_rate ?  res.data.purchase_rate : null;
                return;
            }
            data.value.product_id = null;
            data.value.selling_rate = 0;
            data.value.purchase_rate = null;
            data.value.product_name = "";
        })
            .catch(() => {})
    }
})

const calculateDiscount = () => {
    data.value.discount_amount = (toNumber(amount.value) * toNumber(data.value.discount_percent) / 100).toFixed(4);
}

const calculateVatAmount = () => {
    if (isBlank(data.value.vat)) return;
    data.value.vat_amount = (toNumber(data.value.vat) / 100 * amountAfterDiscount.value).toFixed(4);
}

const onVatChange = () => {
    if (isBlank(data.value.vat)) {
        data.value.vat_amount = null;
        return;
    }
    calculateVatAmount();
}

watch([() => data.value, () => data.value.discount_percent, amount], ([line], [previousLine]) => {
    if (line !== previousLine) return;
    calculateDiscount();
})

watch([() => data.value, () => data.value.vat], ([line], [previousLine]) => {
    if (line !== previousLine) return;
    onVatChange();
})

watch([() => data.value, amountAfterDiscount], ([line], [previousLine]) => {
    if (line !== previousLine) return;
    calculateVatAmount();
})

watch(() => data.value.tax_rate, (taxRate) => {
    if (! taxRate) {
        data.value.tax_rate_id = null;
        return;
    }
    data.value.tax_rate_id = taxRate.id;
})

const onSelectTaxRate = (option) => {
    if (! option) return;
    data.value.vat = Number(option.rate);
}

watch(totalAmount, (newValue) => {
    data.value.g_total = newValue
    data.value.total_amount = newValue
    data.value.net_payable_amount = newValue
})

const focusProductSelect = () => {
    productField.value.dropdownRef.searchEl.focus()
}

const validateData = () => {

    rateError.value = ""
    quantityError.value = ""

    let errorsFound = false;

    if (parseFloat(data.value.rate) > parseFloat(data.value.selling_rate)) {
        rateError.value = "P.rate cannot be more than S.rate";
        errorsFound = true;
    }

    if(!data.value.rate) {
        rateError.value = "Purchase must be more than 0"
        errorsFound = true;
    }

    if(!validateQty()) {
        return errorsFound;
    }

    if(!data.value.quantity) {
        quantityError.value = "Quantity must be more than 0"
        errorsFound = true;
    }

    return errorsFound;
}

const validateQty = () => {
    let quantity = Number(data.value.quantity)

    if (! Number.isInteger(quantity)) {
        quantityError.value = "The quantity must be an Integer";
        data.value.quantity = floor(data.value.quantity)
        qtyField.value.focus();
        return false;
    }

    return true;
}

const getLineToAdd = () => {
    return {
        ...data.value,
        selling_rate: toNumber(data.value.selling_rate),
        discount_percent: toNumber(data.value.discount_percent),
        vat: toNumber(data.value.vat),
        vat_amount: toNumber(data.value.vat_amount),
    };
}

const handleAddProduct = () => {

    if(validateData()) {
        return;
    }
    $emit('handleProductAdd',  getLineToAdd(), updateIndex.value);
    data.value = { ...singleData };
    focusProductSelect();

    if(updateIndex.value !== null && updateIndex.value !== undefined) {
        updateIndex.value = null;
    }
}

const cancelEdit = () => {
    data.value = { ...singleData };
    updateIndex.value = null;
    rateError.value = '';
    quantityError.value = '';
}

const setData = (object) => {
    if(object) {
        data.value = { ...object };
    }
}

defineExpose({updateIndex, setData})

</script>

<style scoped>
.pb-line-form {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 0.75rem;
    align-items: start;
}
.pb-field {
    min-width: 0;
}
.pb-field--wide {
    grid-column: span 2;
}
.pb-field--action {
    grid-column-end: -1;
}
.pb-label {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: #000;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.pb-field .form-control {
    border-radius: 8px;
}
.pb-field :deep(.v-select .vs__dropdown-toggle) {
    border-radius: 8px;
}
.pb-hint {
    display: block;
    margin-top: 0.2rem;
    font-size: 0.72rem;
    color: #6e6b7b;
}
.pb-error {
    display: block;
    margin-top: 0.2rem;
    font-size: 0.72rem;
    color: #ea5455;
}
.pb-actions {
    display: flex;
    gap: 0.5rem;
}
.pb-actions .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    border-radius: 8px;
}
.pb-actions .btn-primary {
    flex: 1 1 auto;
}
@media (max-width: 575.98px) {
    .pb-line-form {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>